import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getOccasionTypes, getOccasionTypeById, updateOccasionType, deleteOccasionType } from "wasp/client/operations";
import { Plus, Search } from "lucide-react";
import DefaultLayout from "./layout/DefaultLayout";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { useFixedPageStack } from "../client/hooks/useFixedPageStack";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "../hooks/use-toast";

export default function OccasionTypesPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: occasionTypes = [], isLoading, error, refetch } = useQuery(getOccasionTypes);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOccasionTypeId, setSelectedOccasionTypeId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    key: '',
    label: '',
    description: '',
    icon: '',
    isActive: true
  });

  // Fetch selected occasion type details for flyout
  const { data: selectedOccasionType, isLoading: loadingOccasionType } = useQuery(
    getOccasionTypeById,
    { id: selectedOccasionTypeId || '' },
    { enabled: !!selectedOccasionTypeId, keepPreviousData: true }
  );

  const filteredData = useMemo(() => {
    return (occasionTypes as any[]).filter((type: any) => {
      if (searchTerm &&
          !type.key.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !type.label.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [occasionTypes, searchTerm]);

  const hasData = filteredData.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: hasData ? 'table-filter' : 'none',
    secondaryRefDeps: [hasData, searchTerm],
  });

  // Initialize edit fields when occasion type is selected
  useEffect(() => {
    if (selectedOccasionType) {
      const type = selectedOccasionType as any;
      setEditFormData({
        key: type.key || '',
        label: type.label || '',
        description: type.description || '',
        icon: type.icon || '',
        isActive: type.isActive ?? true
      });
    }
  }, [selectedOccasionType]);

  // Check if any changes have been made
  const occasionTypeData = selectedOccasionType as any;
  const hasChanges = occasionTypeData ? (
    editFormData.key !== (occasionTypeData.key || '') ||
    editFormData.label !== (occasionTypeData.label || '') ||
    editFormData.description !== (occasionTypeData.description || '') ||
    editFormData.icon !== (occasionTypeData.icon || '') ||
    editFormData.isActive !== (occasionTypeData.isActive ?? true)
  ) : false;

  const handleOccasionTypeClick = (occasionTypeId: string) => {
    setSelectedOccasionTypeId(occasionTypeId);
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!occasionTypeData) return;

    try {
      await updateOccasionType({
        id: occasionTypeData.id,
        ...editFormData
      });

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "Occasion Type updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update occasion type",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!occasionTypeData) return;
    if (confirm('Are you sure you want to delete this occasion type? This action cannot be undone.')) {
      try {
        await deleteOccasionType({ id: occasionTypeData.id });
        setSelectedOccasionTypeId(null);
        await refetch();
        toast({
          title: "Success",
          description: "Occasion Type deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: 'Failed to delete occasion type: ' + (error as Error).message,
          variant: "destructive",
        });
      }
    }
  };

  // Navigation helpers
  const currentOccasionTypeIndex = filteredData.findIndex(t => t.id === selectedOccasionTypeId) ?? -1;
  const hasPrevious = currentOccasionTypeIndex > 0;
  const hasNext = currentOccasionTypeIndex >= 0 && currentOccasionTypeIndex < filteredData.length - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && filteredData) {
      const previousOccasionType = filteredData[currentOccasionTypeIndex - 1];
      setSelectedOccasionTypeId(previousOccasionType.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && filteredData) {
      const nextOccasionType = filteredData[currentOccasionTypeIndex + 1];
      setSelectedOccasionTypeId(nextOccasionType.id);
    }
  };

  return (
    <DefaultLayout user={user}>
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Occasion Types</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Define and manage occasion categories ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => navigate('/admin/occasion-types/new')}
                  icon={Plus}
                  label="Create Type"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-[var(--sidebar-width)] right-0 z-20`}
            onHeightChange={fixedStack.onTableFilterHeightChange}
            columns={[
              { title: 'Key', span: 2 },
              { title: 'Label', span: 3 },
              { title: 'Description', span: 5 },
              { title: 'Occasions', span: 2 }
            ]}
            filters={[
              {
                span: 6,
                content: (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search key or label..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )
              },
              { span: 6, content: <div className="h-10" /> }
            ]}
          />
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading occasion types: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No occasion types found</p>
                <p className="text-sm mt-2">
                  {searchTerm ? "Try adjusting your search" : "Click 'Create Type' to add your first occasion type"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((type: any) => (
                  <div
                    key={type.id}
                    className="px-4 py-2.5 admin-hoverable transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => handleOccasionTypeClick(type.id)}
                    title="Click to view details"
                  >
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                        {type.key}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-900 dark:text-white truncate block">
                        {type.label}
                      </span>
                    </div>
                    <div className="col-span-5 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {type.description || '-'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {(type as any)._count?.occasions || 0} occasions
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedOccasionTypeId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedOccasionTypeId(null);
              setFlyoutMode('view');
            }
          }}
          title={flyoutMode === 'view' ? 'View Occasion Type' : 'Edit Occasion Type'}
          description={occasionTypeData?.key || undefined}
          width="xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === 'view' ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode('view')}
                  className="w-[110px]"
                >
                  {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingOccasionType && !selectedOccasionType ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : occasionTypeData ? (
            <>
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Key"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.key}
                          onChange={(e) => setEditFormData({ ...editFormData, key: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter key (e.g., ramadan, eid)"
                        />
                      ) : (
                        <span>{occasionTypeData.key}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Label"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.label}
                          onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter display label"
                        />
                      ) : (
                        <span>{occasionTypeData.label}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter description"
                        />
                      ) : (
                        <span>{occasionTypeData.description || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Icon"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.icon}
                          onChange={(e) => setEditFormData({ ...editFormData, icon: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter icon name"
                        />
                      ) : (
                        <span>{occasionTypeData.icon || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Settings Section */}
                <FlyoutGridSection title="SETTINGS">
                  <FlyoutGridRow
                    label="Active Status"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isActive}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isActive: checked })}
                          />
                          <span className="text-sm">{editFormData.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <span className={occasionTypeData.isActive ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {occasionTypeData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === 'view' && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Occasions"
                      value={<span>{occasionTypeData._count?.occasions || 0}</span>}
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            </>
          ) : null}
        </AdminFlyoutPane>
      </div>
    </DefaultLayout>
  );
}



